import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Spin } from 'antd';
import { Dropdown, MainMenu } from '@ui-modules';
import { useGlobalContext } from '@context';
import { useFacility, useFeatureFlags, useParamsParse } from '@common/hooks';
import { checkUserLogin, getErrors } from '@common/utils';
import { DocumentsIcon, ManualsIcon, TrainingsIcon } from '@assets/img';
import {
  Booking,
  CarTracking,
  CustomerSatisfaction,
  Financials,
  Fleet,
  MobilityPlanner,
  MyMobility,
  ReportingArea,
} from '@assets/svg/icons';
import { DriverLogo } from '@assets/svg/logos';
import './styles.scss';
import { SessionStorageKeys } from '@common/constants';

const EntryPage: FC = () => {
  const navigate = useNavigate();
  const { agencyName, setAgencyName } = useGlobalContext();
  const { flags } = useFeatureFlags();
  const { parsed, userType } = useParamsParse();
  const { t } = useTranslation();

  const {
    agenciesOptions,
    facility: { city, country, linkFleetManagement, linkVehicleTrackingSystem, name },
    isLoading,
  } = useFacility();

  const [maintenanceModeActive, setMaintenanceModeActive] = useState<boolean>(false);
  const [maintenanceModeText, setMaintenanceModeText] = useState<string>();

  const getMaintenanceMode = async (): Promise<void> => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_HBH_API}/v2/core/feature-flags/`, {
        headers: {
          'X-Subscription-Token': process.env.REACT_APP_SUBSCRIPTION_TOKEN || '',
        },
      });

      const maintenanceMode = response.data?.switches?.maintenance_mode_active;

      if (maintenanceMode) {
        setMaintenanceModeActive(maintenanceMode.is_active);
        setMaintenanceModeText(maintenanceMode.note);
      }
    } catch (e: any) {
      console.error(getErrors(e.response.data));
    }
  };

  const updateAgency = (agency: string): void => {
    setAgencyName(agency);
    sessionStorage.setItem(SessionStorageKeys.SELECTED_AGENCY, agency);
  };

  useEffect(() => {
    getMaintenanceMode();
  }, []);

  useEffect(() => {
    if (
      !sessionStorage.getItem(SessionStorageKeys.AGENCIES) &&
      !sessionStorage.getItem(SessionStorageKeys.FACILITY)
    ) {
      navigate('/forbidden');
    }
  }, [navigate]);

  useEffect(() => {
    if (parsed && userType) {
      checkUserLogin(userType);
    }
  }, [userType, parsed]);

  const menuButtons = [
    {
      label: t('common.booking'),
      className: 'btn-booking',
      icon: <Booking />,
      onClick: () => navigate('/bookings'),
    },
    {
      label: t('common.mobilityPlanner'),
      className: 'btn-mobility-planner',
      onClick: () => navigate('/mobility-planner'),
      icon: <MobilityPlanner />,
    },
    {
      label: t('common.myMobility'),
      className: 'btn-my-mobility',
      icon: <MyMobility />,
      onClick: () => navigate('/mobility-management'),
    },
    {
      label: t('common.financials'),
      className: 'btn-financials',
      icon: <Financials />,
      onClick: () => navigate('/financials'),
    },
    {
      label: t('common.reportingArea'),
      className: 'btn-reporting-area',
      icon: <ReportingArea />,
      onClick: () => navigate('/reporting-area'),
    },
    {
      label: t('common.customerSatisfaction'),
      className: 'btn-customer-satisfaction',
      icon: <CustomerSatisfaction />,
      onClick: () => navigate('/feedbacks'),
    },
  ];

  if (flags.newPlanner?.is_active)
    menuButtons.push({
      label: `NEW ${t('common.mobilityPlanner')}`,
      className: 'btn-mobility-planner',
      onClick: () => navigate('/new-mobility-planner'),
      icon: <MobilityPlanner />,
    });

  if (linkFleetManagement)
    menuButtons.push({
      label: t('common.fleetManagement'),
      className: 'btn-fleet-management',
      icon: <Fleet />,
      onClick: () => window.open(linkFleetManagement),
    });

  if (linkVehicleTrackingSystem)
    menuButtons.push({
      label: t('common.vehicleTrackingSystem'),
      className: 'btn-vehicle-tracking',
      icon: <CarTracking />,
      onClick: () => window.open(linkVehicleTrackingSystem),
    });

  const subMenuButtons = useMemo(
    () => [
      {
        label: t('common.manuals'),
        className: 'btn-manuals',
        icon: <img src={ManualsIcon} alt="manuals" />,
        onClick: () => navigate('manuals', { state: { resource: 'manuals' } }),
      },
      {
        label: t('common.trainings'),
        className: 'btn-trainings',
        icon: <img src={TrainingsIcon} alt="trainings" />,
        onClick: () => navigate('trainings', { state: { resource: 'trainings' } }),
      },
      {
        label: t('common.documents'),
        className: 'btn-documents',
        icon: <img src={DocumentsIcon} alt="documents" />,
        onClick: () => navigate('documents', { state: { resource: 'documents' } }),
      },
    ],
    [navigate, t],
  );

  return (
    <section className="driver-entry-page">
      <div className="driver-entry-header">
        <div className="field field-agency">
          <span>{t('common.agencySelectLabel')} </span>
          <Dropdown
            className="dropdown outline"
            isDisabled={agenciesOptions.length <= 1}
            name="agency"
            options={agenciesOptions}
            value={agenciesOptions.find((option) => option.value === agencyName)}
            onChange={(option) => updateAgency(option.value)}
            data-testid="driver-entry-page-agency-dropdown"
          />
        </div>
      </div>

      {maintenanceModeActive ? (
        <div className="maintenance-disclaimer">{maintenanceModeText}</div>
      ) : null}

      <MainMenu
        logo={isLoading ? <Spin className="spinner" size="large" spinning /> : <DriverLogo />}
        menuButtons={menuButtons}
        subMenuButtons={subMenuButtons}
        title={isLoading ? '' : `${name ? `${name}, ` : ''}${city}, ${country} | ${agencyName}`}
      />
    </section>
  );
};

export default EntryPage;
